import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notBlank } from "../../../util/ext";
import {
  buildOrganizeViewPath,
  getAnthologies,
  getCollections,
  OrganizeViews,
  UNGROUPED_ANTHOLOGY_VIEW,
  UNGROUPED_COLLECTION_VIEW,
} from "../organizeUtil";
import {
  any,
  concat,
  descend,
  equals,
  flatten,
  join,
  map,
  prop,
  propEq,
  sort,
} from "ramda";
import { useSelected } from "../OrganizeProvider";
import { useUnorganizedContentGroups } from "../../creatorServices";
import { useSearchQuery } from "../../common/use-searchQuery";
import { deduplicate } from "../../../util/mediaUtil";

import Button from "@mui/material/Button";

import AddNewCollectionDialog from "../../collections/browse/AddNewCollectionDialog";
import SidebarUngroupedAnthologyTreeItem from "./SidebarUngroupedAnthologyTreeItem";
import SidebarUngroupedCollectionTreeItem from "./SidebarUngroupedCollectionTreeItem";

const buildUngroupedAnthologyNodeId = ({ contentGroup }) =>
  join("-", [
    OrganizeViews.ORGANIZE,
    OrganizeViews.UNGROUPED_ITEMS,
    OrganizeViews.ANTHOLOGY,
    contentGroup.id,
  ]);

const buildUngroupedCollectionNodeId = ({ contentGroup }) =>
  join("-", [
    OrganizeViews.ORGANIZE,
    OrganizeViews.UNGROUPED_ITEMS,
    OrganizeViews.COLLECTION,
    contentGroup.id,
  ]);

const initialContentGroupsState = {
  page: 0,
  limit: 10,
  orderBy: "auditUpdated",
  orderDirection: "desc",
  mediaType: "anthology,collection",
  hasParent: false,
};

const sortByLastUpdated = sort(descend(prop("auditUpdated")));

const SidebarUngroupedContentGroupsTree = props => {
  const navigate = useNavigate();
  const { removeAllSelected, selectedContentGroups } = useSelected();
  const { setLoading, showAlert } = props;

  const contentGroupsQuery = useSearchQuery(initialContentGroupsState);
  const [contentGroupsQueryState, setContentGroupsQueryState] = useState({
    limit: initialContentGroupsState.limit,
    offset: contentGroupsQuery.state.limit * contentGroupsQuery.state.page,
    sort: `${contentGroupsQuery.state.orderBy} ${contentGroupsQuery.state.orderDirection}`,
    mediaType: initialContentGroupsState.mediaType,
    hasParent: false,
  });

  useEffect(() => {
    const { limit, page } = contentGroupsQuery.state;
    setContentGroupsQueryState(prev => ({ ...prev, offset: limit * page }));
  }, [contentGroupsQuery.state]);

  const contentGroupsResult = useUnorganizedContentGroups(
    contentGroupsQueryState,
  );
  const [contentGroups, setContentGroups] = useState([[]]);

  useEffect(() => {
    if (contentGroupsResult.isSuccess) {
      setContentGroups(prev => {
        const previous = contentGroupsQuery.state.page === 0 ? [] : prev;
        const contentGroups = concat(
          previous,
          contentGroupsResult.data.contents,
        );
        return sortByLastUpdated(deduplicate(contentGroups));
      });
    }
  }, [
    contentGroupsQuery.state,
    contentGroupsResult.isSuccess,
    contentGroupsResult.data,
  ]);

  useEffect(() => {
    setLoading(contentGroupsResult.isLoading);
  }, [contentGroupsResult.isLoading, setLoading]);

  const handleLabelClick = ({ id, mediaType }) => event => {
    // if (event.ctrlKey || event.metaKey) {
    //   if (equals(mediaType, "collection")) {
    //     updateSelectedCollections({
    //       id,
    //     });
    //   }

    //   if (equals(mediaType, "anthology")) {
    //     updateSelectedAnthologies({
    //       id,
    //     });
    //   }
    //   return;
    // }

    // removeAllSelectedContent();
    removeAllSelected();
    const view = equals(mediaType, "collection")
      ? UNGROUPED_COLLECTION_VIEW
      : UNGROUPED_ANTHOLOGY_VIEW;
    const viewPath = buildOrganizeViewPath({ view, ids: [id] });
    navigate(viewPath);
  };

  const [open, setOpen] = useState(false);
  // const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);
  const onSave = ({ newCollectionId }) => {
    closeDialog();
    navigate(
      buildOrganizeViewPath({
        view: UNGROUPED_COLLECTION_VIEW,
        ids: [newCollectionId],
      }),
    );
  };

  return (
    <Fragment>
      <AddNewCollectionDialog
        open={open}
        onSave={onSave}
        onClose={closeDialog}
      />

      {notBlank(contentGroups) &&
        map(contentGroup => {
          if (equals(contentGroup.mediaType, "collection")) {
            return (
              <SidebarUngroupedCollectionTreeItem
                collection={contentGroup}
                handleMove={() => contentGroupsQuery.handleUpdatePage(0)}
                key={buildUngroupedCollectionNodeId({ contentGroup })}
                nodeId={buildUngroupedCollectionNodeId({ contentGroup })}
                onLabelClick={handleLabelClick(contentGroup)}
                tooltipLabel={contentGroup.name}
                label={contentGroup.name}
                showAlert={showAlert}
                selected={any(
                  propEq("id", contentGroup.id),
                  getCollections(selectedContentGroups),
                )}
              />
            );
          } else {
            return (
              <SidebarUngroupedAnthologyTreeItem
                child={contentGroup}
                handleMove={() => contentGroupsQuery.handleUpdatePage(0)}
                key={buildUngroupedAnthologyNodeId({ contentGroup })}
                nodeId={buildUngroupedAnthologyNodeId({ contentGroup })}
                parent={contentGroup}
                onLabelClick={handleLabelClick(contentGroup)}
                tooltipLabel={contentGroup.name}
                label={contentGroup.name}
                showAlert={showAlert}
                selected={any(
                  propEq("id", contentGroup.id),
                  getAnthologies(selectedContentGroups),
                )}
              />
            );
          }
        }, flatten(contentGroups))}

      {contentGroupsResult.isSuccess && contentGroupsResult.data.hasMore && (
        <Button
          style={{ width: "95%" }}
          onClick={() =>
            contentGroupsQuery.handleUpdatePage(
              contentGroupsQuery.state.page + 1,
            )
          }
        >
          More
        </Button>
      )}
    </Fragment>
  );
};

export default SidebarUngroupedContentGroupsTree;
